// Binary tile sidecar (sidecarFormat 2) - the contract frozen with the
// exporter on 2026-09-08.
//
//   magic "BLSC" | u32 version (2) | u32 rowCount | u32 hashCount
//   u32 jsonLen | utf8 JSON header: { hashes[], fragmentUrls[] (aligned
//       with hashes), uids[], docs[], outlier, bbox:{min,max} }
//   then 4-byte-aligned sections, little-endian, in this order:
//     hashRowStart u32[hashCount+1]   rows of hash i = [start[i], start[i+1])
//     rowHash      u32[rowCount]      index into hashes
//     rowUid       u32[rowCount]      index into uids
//     rowDoc       u16[rowCount]      index into docs, 0xFFFF = none
//     rowFlags     u8 [rowCount]      bit0 = disabled
//     matrices     f32[rowCount*16]   column-major three.js order
//
// Rows are sorted by hash (then uid), so one hash's matrices are a
// contiguous Float32 range. decode() returns the same shape the JSON
// sidecar has ({ instances, fragmentUrls, outlier, bbox }) so
// TileRegistry.hydrate needs no second path; each row carries
// `m` = a Float32Array view into the matrices block and `cm: true`
// so GlobalInstancePool.acquire copies it without the row-major
// transpose the JSON rows need.
const MAGIC = 0x43534c42; // "BLSC" little-endian
export const SIDECAR_BINARY_VERSION = 2;

function align4(n) { return (n + 3) & ~3; }

export function decodeSidecarBinary(arrayBuffer) {
  if (!(arrayBuffer instanceof ArrayBuffer) || arrayBuffer.byteLength < 20) {
    throw new Error('sidecarBinary: buffer too small');
  }
  const dv = new DataView(arrayBuffer);
  if (dv.getUint32(0, true) !== MAGIC) throw new Error('sidecarBinary: bad magic');
  const version = dv.getUint32(4, true);
  if (version !== SIDECAR_BINARY_VERSION) throw new Error(`sidecarBinary: unsupported version ${version}`);
  const rowCount = dv.getUint32(8, true);
  const hashCount = dv.getUint32(12, true);
  const jsonLen = dv.getUint32(16, true);
  let off = 20;
  const header = JSON.parse(new TextDecoder('utf-8').decode(new Uint8Array(arrayBuffer, off, jsonLen)));
  off = align4(off + jsonLen);

  const need = (bytes) => {
    if (off + bytes > arrayBuffer.byteLength) throw new Error('sidecarBinary: truncated');
  };
  need((hashCount + 1) * 4);
  const hashRowStart = new Uint32Array(arrayBuffer, off, hashCount + 1); off = align4(off + (hashCount + 1) * 4);
  need(rowCount * 4);
  const rowHash = new Uint32Array(arrayBuffer, off, rowCount); off = align4(off + rowCount * 4);
  need(rowCount * 4);
  const rowUid = new Uint32Array(arrayBuffer, off, rowCount); off = align4(off + rowCount * 4);
  need(rowCount * 2);
  const rowDoc = new Uint16Array(arrayBuffer, off, rowCount); off = align4(off + rowCount * 2);
  need(rowCount);
  const rowFlags = new Uint8Array(arrayBuffer, off, rowCount); off = align4(off + rowCount);
  need(rowCount * 64);
  const matrices = new Float32Array(arrayBuffer, off, rowCount * 16);

  const hashes = header.hashes || [];
  const uids = header.uids || [];
  const urls = header.fragmentUrls || [];
  const fragmentUrls = {};
  for (let i = 0; i < hashes.length; i++) if (urls[i]) fragmentUrls[hashes[i]] = urls[i];

  // Layout sniff (2026-09-09): the first prod .bin publish wrote the
  // matrices in the JSON (row-major) order and every instance rendered
  // stretched towards one point, because this reader skips the
  // transpose the contract makes unnecessary. A column-major affine
  // matrix keeps its translation in m[12..14] with m[3], m[7], m[11]
  // exactly 0; the row-major layout is the other way round. Decide once
  // per file from rows that carry a translation, and transpose on read
  // when the file is row-major - correct but warned, instead of broken.
  let rowMajorVotes = 0; let columnMajorVotes = 0;
  for (let i = 0; i < rowCount && (rowMajorVotes + columnMajorVotes) < 64; i++) {
    const o = i * 16;
    const bottomRow = Math.abs(matrices[o + 3]) + Math.abs(matrices[o + 7]) + Math.abs(matrices[o + 11]);
    const lastCol = Math.abs(matrices[o + 12]) + Math.abs(matrices[o + 13]) + Math.abs(matrices[o + 14]);
    if (bottomRow > 1e-6 && lastCol <= 1e-6) rowMajorVotes++;
    else if (lastCol > 1e-6 && bottomRow <= 1e-6) columnMajorVotes++;
  }
  const rowMajor = rowMajorVotes > 0 && rowMajorVotes >= columnMajorVotes;
  if (rowMajor) {
    console.warn(`[sidecarBinary] matrices are row-major (JSON order) in a format-2 sidecar; transposing on read (${rowMajorVotes} rows voted)`);
  }

  // The rows' matrices are views into ONE compact copy of the matrix
  // block, not into the downloaded buffer: a view keeps its whole buffer
  // alive, and the buffer also carries the uid table, the hash index and
  // the JSON header - on SpaceX 231 MB held for 93 MB of matrices
  // (2026-09-18). The copy is rowCount * 64 bytes.
  const block = new Float32Array(matrices);
  // NESTED PARTS (Tom, 2026-09-24). `parents` is aligned with the UID
  // TABLE, not with the rows: parents[i] is the parent uid of uids[i], or
  // null for a thing that is an element in its own right. Present only in
  // tiles that hold a part, so every other tile is byte-identical and an
  // older decoder simply ignores it - which is why this needed no format
  // bump.
  const parents = Array.isArray(header.parents) ? header.parents : null;
  // CLEARANCE (Tom, e5d0c7b, 2026-09-25). The header carries an array of
  // indexes INTO THE UID TABLE, not a per-row flag - which is why looking for
  // a flag bit finds nothing and concludes, wrongly, that the field does not
  // exist. It is OMITTED from tiles that hold no clearance (788 of 1,445 on
  // the live SpaceX export), so its absence in any one tile says nothing
  // about the format. The JSON sidecar writes `c: true` per row from the same
  // call, so `c` is the shared signal and this is where the binary side
  // earns it.
  const clearanceUids = Array.isArray(header.clearance) && header.clearance.length
    ? new Set(header.clearance) : null;
  const instances = new Array(rowCount);
  for (let i = 0; i < rowCount; i++) {
    const doc = rowDoc[i];
    const parent = parents ? parents[rowUid[i]] : null;
    instances[i] = {
      u: uids[rowUid[i]],
      // `p` only when this row IS a part. Undefined is the common case and
      // costs nothing; a null would be a field on every row of every tile.
      ...(parent ? { p: parent } : null),
      h: hashes[rowHash[i]],
      d: (rowFlags[i] & 1) === 1,
      // Only set when true, so a row that is not clearance carries no extra
      // field - the same reason `p` is omitted rather than nulled.
      ...(clearanceUids && clearanceUids.has(rowUid[i]) ? { c: true } : null),
      m: block.subarray(i * 16, i * 16 + 16),
      // cm:false makes GlobalInstancePool.acquire transpose, exactly as
      // it does for JSON rows.
      cm: !rowMajor,
      f: doc === 0xFFFF ? undefined : doc,
    };
  }
  return {
    format: SIDECAR_BINARY_VERSION,
    layout: rowMajor ? 'row-major' : 'column-major',
    instances,
    fragmentUrls,
    outlier: !!header.outlier,
    bbox: header.bbox || null,
    // Copies, not views, so nothing returned pins the downloaded buffer.
    hashes, hashRowStart: new Uint32Array(hashRowStart), matrices: block, docs: header.docs || [],
  };
}

// Test / tooling helper: build a buffer from rows the way the exporter
// will. Rows are sorted by hash then uid here so the invariant holds.
export function encodeSidecarBinary({ rows, fragmentUrls = {}, outlier = false, bbox = null, docs = [] }) {
  const sorted = [...rows].sort((a, b) => (a.h < b.h ? -1 : a.h > b.h ? 1 : a.u < b.u ? -1 : a.u > b.u ? 1 : 0));
  const hashes = []; const hashIndex = new Map();
  const uids = []; const uidIndex = new Map();
  for (const r of sorted) {
    if (!hashIndex.has(r.h)) { hashIndex.set(r.h, hashes.length); hashes.push(r.h); }
    if (!uidIndex.has(r.u)) { uidIndex.set(r.u, uids.length); uids.push(r.u); }
  }
  // parents[] aligned with uids[], null where the uid is an element.
  // Written only when at least one row is a part, so a tile with no
  // nesting is byte-identical to one built before this existed.
  const parents = uids.map((u) => {
    const row = sorted.find((r) => r.u === u && r.p);
    return row ? row.p : null;
  });
  const header = { hashes, fragmentUrls: hashes.map((h) => fragmentUrls[h] || ''), uids, docs, outlier, bbox };
  if (parents.some(Boolean)) header.parents = parents;
  const json = new TextEncoder().encode(JSON.stringify(header));
  const n = sorted.length; const hc = hashes.length;
  let size = 20 + json.length; size = align4(size);
  size += align4((hc + 1) * 4) + align4(n * 4) + align4(n * 4) + align4(n * 2) + align4(n) + n * 64;
  const buf = new ArrayBuffer(size);
  const dv = new DataView(buf);
  dv.setUint32(0, MAGIC, true); dv.setUint32(4, SIDECAR_BINARY_VERSION, true);
  dv.setUint32(8, n, true); dv.setUint32(12, hc, true); dv.setUint32(16, json.length, true);
  new Uint8Array(buf, 20, json.length).set(json);
  let off = align4(20 + json.length);
  const hashRowStart = new Uint32Array(buf, off, hc + 1); off = align4(off + (hc + 1) * 4);
  const rowHash = new Uint32Array(buf, off, n); off = align4(off + n * 4);
  const rowUid = new Uint32Array(buf, off, n); off = align4(off + n * 4);
  const rowDoc = new Uint16Array(buf, off, n); off = align4(off + n * 2);
  const rowFlags = new Uint8Array(buf, off, n); off = align4(off + n);
  const matrices = new Float32Array(buf, off, n * 16);
  let cur = -1;
  sorted.forEach((r, i) => {
    const hi = hashIndex.get(r.h);
    while (cur < hi) { cur++; hashRowStart[cur] = i; }
    rowHash[i] = hi; rowUid[i] = uidIndex.get(r.u);
    rowDoc[i] = (r.f === undefined || r.f === null) ? 0xFFFF : r.f;
    rowFlags[i] = r.d ? 1 : 0;
    matrices.set(r.m, i * 16);
  });
  while (cur < hc) { cur++; hashRowStart[cur] = n; }
  return buf;
}
